<div class="migration-logs-panel p-2">
    <!-- Toolbar -->
    <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 class="text-base font-semibold text-gray-700">Migration Sync Logs</h3>
        <div class="flex items-center gap-3">
            <mat-form-field appearance="fill" class="w-44" subscriptSizing="dynamic">
                <mat-label>Status</mat-label>
                <mat-select [(ngModel)]="statusFilter" [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onStatusChange()">
                    <mat-option [value]="undefined">All</mat-option>
                    @for (s of statusOptions; track s) {
                    <mat-option [value]="s">{{ s }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>
            <button mat-icon-button (click)="loadLogs()" [disabled]="isLoading()" matTooltip="Refresh">
                <mat-icon>refresh</mat-icon>
            </button>
        </div>
    </div>

    <!-- Loading -->
    @if (isLoading()) {
    <div class="flex justify-center py-8">
        <mat-spinner diameter="40"></mat-spinner>
    </div>
    } @else if (logs().length === 0) {
    <div class="text-center py-8 text-gray-500">
        <mat-icon class="text-5xl text-gray-300">sync_disabled</mat-icon>
        <p class="mt-2">No sync logs found.</p>
    </div>
    } @else {
    <div class="overflow-x-auto">
        <table mat-table [dataSource]="logs()" class="w-full text-sm">

            <!-- startedAt -->
            <ng-container matColumnDef="startedAt">
                <th mat-header-cell *matHeaderCellDef class="whitespace-nowrap">Started At</th>
                <td mat-cell *matCellDef="let row">{{ row.startedAt | date:'short' }}</td>
            </ng-container>

            <!-- completedAt -->
            <ng-container matColumnDef="completedAt">
                <th mat-header-cell *matHeaderCellDef class="whitespace-nowrap">Completed At</th>
                <td mat-cell *matCellDef="let row">
                    @if (row.completedAt) {
                    {{ row.completedAt | date:'short' }}
                    } @else {
                    <span class="text-gray-400">—</span>
                    }
                </td>
            </ng-container>

            <!-- status -->
            <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let row">
                    <span class="px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                'bg-green-100 text-green-800': row.status === 'Completed',
                'bg-red-100 text-red-800': row.status === 'Failed',
                'bg-blue-100 text-blue-800': row.status === 'Running',
                'bg-gray-100 text-gray-700': row.status !== 'Completed' && row.status !== 'Failed' && row.status !== 'Running'
              }">
                        {{ row.status ?? '—' }}
                    </span>
                </td>
            </ng-container>

            <!-- parkersCreated -->
            <ng-container matColumnDef="parkersCreated">
                <th mat-header-cell *matHeaderCellDef class="text-right whitespace-nowrap">Parkers Created</th>
                <td mat-cell *matCellDef="let row" class="text-right">
                    <span [matTooltip]="'Failed: ' + (row.parkersFailed ?? 0)">{{ row.parkersCreated ?? 0 }}</span>
                </td>
            </ng-container>

            <!-- licensePlates -->
            <ng-container matColumnDef="licensePlates">
                <th mat-header-cell *matHeaderCellDef class="text-right whitespace-nowrap">LPs Created</th>
                <td mat-cell *matCellDef="let row" class="text-right">
                    <span
                        [matTooltip]="'Linked: ' + (row.licensePlatesLinked ?? 0) + '  Skipped: ' + (row.licensePlatesSkipped ?? 0) + '  Failed: ' + (row.licensePlatesFailed ?? 0)">
                        {{ row.licensePlatesCreated ?? 0 }}
                    </span>
                </td>
            </ng-container>

            <!-- orgsCreated -->
            <ng-container matColumnDef="orgsCreated">
                <th mat-header-cell *matHeaderCellDef class="text-right whitespace-nowrap">Orgs Created</th>
                <td mat-cell *matCellDef="let row" class="text-right">
                    <span [matTooltip]="'Failed: ' + (row.orgsFailed ?? 0)">{{ row.orgsCreated ?? 0 }}</span>
                </td>
            </ng-container>

            <!-- parkingRightsImported (nonPoolImported + poolRightsCreated) -->
            <ng-container matColumnDef="parkingRightsImported">
                <th mat-header-cell *matHeaderCellDef class="text-right whitespace-nowrap">Rights Imported</th>
                <td mat-cell *matCellDef="let row" class="text-right">
                    <span
                        [matTooltip]="'Non-pool: ' + (row.nonPoolImported ?? 0) + '  Pool: ' + (row.poolRightsCreated ?? 0) + '  Failed: ' + ((row.nonPoolFailed ?? 0) + (row.poolRightsFailed ?? 0))">
                        {{ (row.nonPoolImported ?? 0) + (row.poolRightsCreated ?? 0) }}
                    </span>
                </td>
            </ng-container>

            <!-- errorMessage -->
            <ng-container matColumnDef="errorMessage">
                <th mat-header-cell *matHeaderCellDef>Error</th>
                <td mat-cell *matCellDef="let row">
                    @if (row.errorMessage) {
                    <span class="text-red-600 text-xs truncate max-w-xs block" [matTooltip]="row.errorMessage">
                        {{ row.errorMessage }}
                    </span>
                    } @else {
                    <span class="text-gray-400">—</span>
                    }
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="hover:bg-gray-50"></tr>
        </table>
    </div>

    <!-- Pagination -->
    <div class="flex items-center justify-end gap-4 mt-3 text-sm text-gray-600">
        <span>{{ pageStart }}–{{ pageEnd }} of {{ totalCount() }}</span>
        <button mat-icon-button [disabled]="!hasPrevPage" (click)="prevPage()">
            <mat-icon>chevron_left</mat-icon>
        </button>
        <button mat-icon-button [disabled]="!hasNextPage" (click)="nextPage()">
            <mat-icon>chevron_right</mat-icon>
        </button>
    </div>
    }
</div>